import type { TFunction } from "i18next";

import {
	Alert,
	AlertTitle,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemText
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { CancelButton } from "~/components/Buttons";
import { CaptionText } from "~/components/Typographies";
import { type UsedBySubject } from "~/components/usedBy/usedByHelpers";
import { useUsedByPanel } from "~/providers/UsedByPanelContext";

export interface BatchOutcomeSummary {
	appliedIdentities?: string[];
	failedCount: number;
	failures: string[];
	notReachedCount?: number;
	refused?: UsedBySubject[];
	skipped?: string[];
	skippedCount?: number;
	totalCount: number;
}

interface BatchResultsDialogProps {
	onClose: () => void;
	open: boolean;
	result: BatchOutcomeSummary | null;
}

const summaryLine = (
	result: BatchOutcomeSummary,
	skippedCount: number,
	notReachedCount: number,
	t: TFunction<"common">
): string =>
	[
		skippedCount > 0 ? t("batchResults.skipped", { skippedCount }) : null,
		result.failedCount > 0 ? t("batchResults.failed", { failedCount: result.failedCount }) : null,
		notReachedCount > 0 ? t("batchResults.notReached", { notReachedCount }) : null
	]
		.filter((part): part is string => part !== null)
		.join(" · ");

const BatchResultsDialog = ({ onClose, open, result }: BatchResultsDialogProps) => {
	const { t } = useTranslation("common");
	const { openUsedBy } = useUsedByPanel();

	if (!result) return null;

	const skippedCount = result.skippedCount ?? 0;
	const notReachedCount = result.notReachedCount ?? 0;
	const appliedIdentities = result.appliedIdentities ?? [];
	const successCount = result.totalCount - result.failedCount - skippedCount - notReachedCount;
	const notAppliedCount = result.totalCount - successCount;
	const severity =
		result.failedCount === 0
			? skippedCount > 0 || notReachedCount > 0
				? "warning"
				: "success"
			: successCount > 0
				? "warning"
				: "error";

	return (
		<Dialog maxWidth="sm" fullWidth onClose={onClose} open={open}>
			<DialogTitle>{t("batchResults.title")}</DialogTitle>
			<DialogContent>
				<Alert severity={severity}>
					<AlertTitle>{t("batchResults.succeeded", { successCount, totalCount: result.totalCount })}</AlertTitle>
					{summaryLine(result, skippedCount, notReachedCount, t)}
				</Alert>
				{notReachedCount > 0 && (
					<CaptionText color="text.secondary" sx={{ display: "block", pt: 1 }}>
						{t("batchResults.stoppedBeforeCompletion", { appliedCount: successCount, notAppliedCount })}
					</CaptionText>
				)}
				{notReachedCount > 0 && appliedIdentities.length > 0 && (
					<List
						dense
						subheader={<CaptionText color="text.secondary">{t("batchResults.appliedSubheader")}</CaptionText>}
					>
						{appliedIdentities.map((identity, index) => (
							<ListItem key={index}>
								<ListItemText primary={identity} />
							</ListItem>
						))}
					</List>
				)}
				{result.skipped && result.skipped.length > 0 && (
					<List dense>
						{result.skipped.map((item, index) => (
							<ListItem key={index}>
								<ListItemText primary={item} />
							</ListItem>
						))}
					</List>
				)}
				{result.failures.length > 0 && (
					<List dense>
						{result.failures.map((failure, index) => (
							<ListItem key={index}>
								<ListItemText primary={failure} />
							</ListItem>
						))}
					</List>
				)}
				{result.refused && result.refused.length > 0 && (
					<List dense>
						{result.refused.map((subject) => (
							<ListItem
								key={`${subject.kind}:${subject.id}`}
								secondaryAction={
									<Button
										onClick={() => {
											onClose();
											openUsedBy(subject);
										}}
										size="small"
									>
										{t("usedBy.seeWhatUsesIt")}
									</Button>
								}
							>
								<ListItemText primary={subject.name} />
							</ListItem>
						))}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<CancelButton onClick={onClose}>{t("actions.close")}</CancelButton>
			</DialogActions>
		</Dialog>
	);
};

export default BatchResultsDialog;
